import * as React from 'react';
import { Popover as PopoverPrimitive } from '@base-ui/react/popover';

import { cn } from '../lib/utils';

function Popover({ ...props }: Readonly<PopoverPrimitive.Root.Props>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

/* VOLL: o LIMITE INFERIOR dos popovers de uma região (2026-08-19). Nasceu pro shell conversacional:
   lá o chão útil da tela não é a borda da janela, é o TOPO do composer — dropdown que desce até a
   janela cobre o campo de digitar. O contexto carrega o ref do elemento-chão; o `PopoverContent`
   dentro do provider converte o topo dele em `collisionPadding.bottom`, então a dropdown encolhe
   (via `--available-height`) antes de encostar nele. Fora do provider nada muda: o limite segue
   sendo o viewport. */
const BottomBoundaryContext = React.createContext<React.RefObject<HTMLElement | null> | null>(null);

function PopoverBottomBoundary({
  boundaryRef,
  children,
}: {
  /** O elemento cujo TOPO é o chão dos popovers da subárvore (a saia do composer, no shell). */
  boundaryRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}) {
  return (
    <BottomBoundaryContext.Provider value={boundaryRef}>{children}</BottomBoundaryContext.Provider>
  );
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  // VOLL: `anchor` exposto do Positioner — ancora o popover num ELEMENTO externo (um pino do mapa)
  // em vez do Trigger. É o que permite popover controlado sem gatilho próprio.
  anchor,
  // VOLL: o desvio de colisão, sobrescrevível — o default segue o de sempre (ver a nota no
  // Positioner). Existe pro consumidor que precisa TRAVAR o lado: `{ side: 'none' }` desliga o
  // flip bottom↔top quando o painel deve nascer sempre pra baixo, custe o que custar.
  // MESCLADO e não substituído: o que o consumidor manda entra POR CIMA do default, chave a chave.
  // Objeto trocado inteiro faria `{ side: 'none' }` devolver o `fallbackAxisSide: 'end'` do Base UI
  // pela porta dos fundos — justo o salto pro eixo perpendicular que a nota do Positioner proíbe.
  collisionAvoidance,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset' | 'anchor' | 'collisionAvoidance'
  >) {
  const limiteInferior = React.useContext(BottomBoundaryContext);
  /* Lido no RENDER do popup — que monta a cada abertura, então a medida é fresca no momento que
     importa. O padding não reage a resize com o popover aberto (o chão do conversacional não muda
     de altura enquanto a pessoa está na dropdown); os demais lados ficam nos 5px default. */
  const chao = limiteInferior?.current;
  const collisionPadding = chao
    ? {
        top: 5,
        left: 5,
        right: 5,
        bottom: Math.max(5, window.innerHeight - chao.getBoundingClientRect().top + 5),
      }
    : undefined;
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        anchor={anchor}
        collisionPadding={collisionPadding}
        /* VOLL: sem fallback pro eixo perpendicular (2026-08-19). O default do Base UI
           (`fallbackAxisSide: 'end'`) fazia a dropdown SALTAR pro lado do campo quando nem
           bottom nem top comportavam a altura toda — mas os painéis já encolhem via
           `--available-height` e rolam por dentro, então "não coube" nunca é verdade aqui.
           O flip bottom↔top continua valendo. */
        collisionAvoidance={{ fallbackAxisSide: 'none', ...collisionAvoidance }}
        // `genui2`: o Positioner sai por Portal, fora da raiz .genui2 — sem a classe
        // ele herda os tokens do CSS legado do host. Ver AGENTS.md desta pasta.
        className="genui2 isolate z-(--z-popup)"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            'z-50 flex w-72 origin-(--transform-origin) flex-col gap-2.5 rounded-lg bg-popover p-2.5 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

// VOLL: o Close do Base UI, exposto — é o que deixa um rodapé "Confirmar" fechar o popover sem o
// consumidor controlar `open` na mão. Padrão `render=` igual ao Trigger.
function PopoverClose({ ...props }: Readonly<PopoverPrimitive.Close.Props>) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="popover-header"
      className={cn('flex flex-col gap-0.5 text-sm', className)}
      {...props}
    />
  );
}

function PopoverTitle({ className, ...props }: Readonly<PopoverPrimitive.Title.Props>) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn('font-medium', className)}
      {...props}
    />
  );
}

function PopoverDescription({ className, ...props }: Readonly<PopoverPrimitive.Description.Props>) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Popover,
  PopoverBottomBoundary,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
};
